import { useMemo } from "react";
import { searchMarkets } from "@/api/polymarketApi";
import { Chip, Empty, GridNote, OutcomeTag, Panel, SearchBox, Select, SourceError, Toggle } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { MARKETS_REFRESH_MS, useFlaggedFeed, useLargeTrades, useLive, useRankedMarkets } from "../lib/data";
import { Updated } from "../lib/freshness";
import { blankText, ago, cents, centsDelta, count, isoDay, toneClass, usd } from "../lib/format";
import { categoryOptions, type DeskMarket, matches, normalizeMarket } from "../lib/markets";
import { openMarket, setParams, useParam, useSearchParam } from "../lib/nav";
import { classify, type MarketGroup, multiple, type Ranked, type ScoreParts, whySentence } from "../lib/unusual";

const MIN_VOL24 = 10_000;
const SPIKE_X = 2;
const WHALE_MIN_USD = 10_000;

interface Row extends DeskMarket {
	whales: number;
	flagged: number;
	group: MarketGroup;
	note: string | null;
	parts: ScoreParts | null;
	siblings: number;
	folded: boolean;
}

const TABS: { id: MarketGroup; label: string; title: string; desc: string }[] = [
	{
		id: "ranked",
		label: "unusual",
		title: "No scheduled reason to be busy",
		desc: "No scheduled reason to be busy, yet volume or price is off its own baseline and its category.",
	},
	{
		id: "scheduled",
		label: "scheduled",
		title: "Fixtures, and markets ending within 48h",
		desc: "Fixtures, and markets ending within 48h. Volume here follows the calendar, so these are not scored.",
	},
	{
		id: "new",
		label: "new",
		title: "Under 7 days old",
		desc: "Opened in the last 7 days. Too new to have a baseline, so these are not scored.",
	},
	{
		id: "past_end",
		label: "past end date",
		title: "End date has passed, still open",
		desc: "End date has passed but the market is still open, usually waiting on a result. Prices may already reflect the outcome.",
	},
];

const pts = (v: number) => `${v < 0 ? "−" : "+"}${Math.abs(v).toFixed(0)}`;

function partsTitle(m: Row, p: ScoreParts): string {
	return [
		`Volume ${multiple(m.volX, p.thinBaseline)} its usual ${usd(m.baselineDaily)} a day: ${pts(p.volumePts)}`,
		`${p.peerLabel} median ${multiple(p.peerVolX, false)} across ${p.peerCount} markets: ${pts(p.peerPts)}`,
		`24h price move beyond peers: ${pts(p.pricePts)}`,
		p.discountWhy ? `Discount ×${p.discount.toFixed(2)}: ${p.discountWhy}` : null,
	].filter(Boolean).join("\n");
}

function Bar({ label, value, max, tone }: { label: string; value: number; max: number; tone: string }) {
	return (
		<span className="flex items-center gap-1">
			<span className="text-[9px] text-mute">{label}</span>
			<span className="relative h-1.5 w-7 bg-ink">
				<span className={`absolute inset-y-0 left-0 ${tone}`} style={{ width: `${Math.min(100, (Math.abs(value) / max) * 100)}%` }} />
			</span>
		</span>
	);
}

function Parts({ m }: { m: Row }) {
	const p = m.parts;
	if (!p) return <span className="text-dim">{m.note ?? "–"}</span>;
	return (
		<span className="flex items-center gap-1.5" title={partsTitle(m, p)}>
			<Bar label="vol" value={p.volumePts} max={60} tone="bg-up" />
			<Bar label="peers" value={p.peerPts} max={60} tone="bg-dim" />
			<Bar label="price" value={p.pricePts} max={40} tone="bg-info" />
			{p.discount < 1 ? <span className="text-[10px] text-warn" title={p.discountWhy ?? undefined}>×{p.discount.toFixed(1)}</span> : null}
		</span>
	);
}

function Signals({ m }: { m: Row }) {
	return (
		<span className="flex items-center gap-1 overflow-hidden">
			{m.whales > 0 ? <Chip tone="info" title={`Trades of ${usd(WHALE_MIN_USD)}+ in the loaded tape. It covers a limited window, so no chip does not mean no whales.`}>{m.whales} whale</Chip> : null}
			{m.flagged > 0 ? <Chip tone="flag" title="Flagged positions in the loaded feed">FLAG {m.flagged}</Chip> : null}
			{m.siblings > 0 ? <Chip title={`${m.siblings} more ranked markets from this event are folded under this row`}>+{m.siblings}</Chip> : null}
		</span>
	);
}

const MARKET_COL: Col<Row> = {
	key: "q",
	label: "Market",
	w: 150,
	grow: 4,
	sort: (m) => m.question,
	cell: (m, narrow) => <span className={narrow ? "clamp-2 text-bright" : "text-bright"} title={m.question}>{m.question}</span>,
};
const SCORE_COL: Col<Row> = {
	key: "u",
	label: "Score",
	title: "0 to 100: volume above the market's own baseline, less its category's median, plus the price move, scaled down if the market ends soon or is young. Open a row for the parts.",
	w: 54,
	right: true,
	sort: (m) => m.parts?.score,
	cell: (m) => <span className="font-bold text-bright">{m.parts ? m.parts.score.toFixed(0) : "–"}</span>,
};
const COMMON_COLS: Col<Row>[] = [
	{ key: "o", label: "Outcome", title: "The first outcome, which Now and 24h refer to", w: 62, prio: 5, csv: (m) => m.outcomes[0]?.name, cell: (m) => (m.outcomes[0] ? <OutcomeTag name={m.outcomes[0].name} /> : "–") },
	{ key: "p", label: "Now", title: "Price of the first outcome", w: 48, right: true, prio: 2, sort: (m) => m.outcomes[0]?.price, cell: (m) => <span className="text-bright">{cents(m.outcomes[0]?.price)}</span> },
	{ key: "d", label: "24h", title: "24h price change of the first outcome", w: 56, right: true, prio: 3, sort: (m) => m.change24, cell: (m) => <span className={toneClass(m.change24)}>{centsDelta(m.change24)}</span> },
	{
		key: "x",
		label: "×avg",
		title: "24h volume over the busier of the market's 7-day and 30-day daily averages. ≥ means that average was under $1K, so it counts as $1K. Blank under 3 days old.",
		w: 46,
		right: true,
		sort: (m) => m.volX,
		// Only a scored market's multiple means anything: on a fixture, 400× is just match day.
		cell: (m) => <span className={m.parts && m.volX !== null && m.volX >= SPIKE_X ? "font-bold text-up" : "text-dim"}>{multiple(m.volX, m.parts?.thinBaseline ?? false)}</span>,
	},
];
const TAIL_COLS: Col<Row>[] = [
	{ key: "s", label: "Signals", title: "Whale chips count trades of $10K+ in the last 500-trade tape, roughly the last 12h, so no chip does not mean no whales. FLAG counts flagged positions in the loaded feed. +N is how many more markets from this event are folded under the row.", w: 96, prio: 6, cell: (m) => <Signals m={m} /> },
	{ key: "v", label: "Vol 24h", title: "Dollars traded in the last 24 hours", w: 62, right: true, prio: 3, sort: (m) => m.vol24, cell: (m) => usd(m.vol24) },
	{ key: "e", label: "Ends", title: "End date Polymarket lists, which can be an estimate", w: 76, right: true, prio: 7, sort: (m) => m.endDate, cell: (m) => <span className="text-dim">{isoDay(m.endDate)}</span> },
	{ key: "c", label: "Category", w: 74, right: true, prio: 8, sort: (m) => m.category, cell: (m) => <span className="text-dim">{m.category ?? "–"}</span> },
];
const SCORED_COLS: Col<Row>[] = [
	MARKET_COL,
	SCORE_COL,
	...COMMON_COLS,
	{ key: "w", label: "Why", title: "How much each part adds. Hover for the numbers; open a row for the full breakdown.", w: 214, prio: 4, wideOnly: true, sort: (m) => m.parts?.pricePts, cell: (m) => <Parts m={m} /> },
	...TAIL_COLS,
];
const PLAIN_COLS: Col<Row>[] = [
	MARKET_COL,
	...COMMON_COLS,
	{ key: "w", label: "Status", w: 176, prio: 4, wideOnly: true, sort: (m) => m.note, cell: (m) => <Parts m={m} /> },
	...TAIL_COLS,
];

const toRow = (r: Ranked, whales: Map<string, number>, flags: Map<string, number>): Row => ({
	...r.market,
	group: r.group,
	note: r.note,
	parts: r.parts,
	siblings: r.siblings,
	folded: r.folded,
	whales: whales.get(r.market.conditionId) ?? 0,
	flagged: flags.get(r.market.conditionId) ?? 0,
});

export function UnusualMarketsView() {
	const [q, setQ, settledQ] = useSearchParam("q", "search");
	const [tabParam] = useParam("tab", "ranked");
	const tab = TABS.find((t) => t.id === tabParam) ?? (TABS[0] as (typeof TABS)[number]);
	const [category, setCategory] = useParam("cat", "all");
	const [showSettled, setShowSettled] = useParam("settled");
	const [showFolded, setShowFolded] = useParam("all");

	const { markets, ranked, partial, error, isLoading, mutate, updatedAt } = useRankedMarkets();
	const { data: trades } = useLargeTrades(WHALE_MIN_USD);
	const flagged = useFlaggedFeed();

	const { data: found } = useLive(settledQ.length >= 3 ? ["pm:search", settledQ] : null, (signal) => searchMarkets(settledQ, signal));

	const rows = useMemo<Row[]>(() => {
		const whales = new Map<string, number>();
		for (const t of trades ?? []) whales.set(t.condition_id, (whales.get(t.condition_id) ?? 0) + 1);
		const flags = new Map<string, number>();
		for (const f of flagged.rows) {
			if (f.conditionId) flags.set(f.conditionId, (flags.get(f.conditionId) ?? 0) + 1);
		}
		const list = ranked.map((r) => toRow(r, whales, flags));
		if (!settledQ) return list;
		// Search hits outside the loaded set are grouped but unscored: they have no peers here.
		const seen = new Set(markets.map((m) => m.conditionId));
		const now = Date.now();
		const extra = (found ?? []).map((m) => normalizeMarket(m, now)).filter((m) => !seen.has(m.conditionId));
		return [...list, ...extra.map((market) => toRow({ market, ...classify(market, now), parts: null, siblings: 0, folded: false }, whales, flags))];
	}, [ranked, markets, trades, flagged.rows, found, settledQ]);

	const categories = useMemo(() => {
		const names = [...new Set(markets.map((m) => m.category).filter((c): c is string => Boolean(c)))].sort();
		return categoryOptions(names, category);
	}, [markets, category]);

	const inDefaultList = (m: Row) =>
		m.vol24 >= MIN_VOL24 && (m.group === "past_end" || showSettled === "1" || !m.settled) && (showFolded === "1" || !m.folded);

	const searching = q.trim().length > 0;
	const counts = useMemo(() => {
		const c: Record<string, number> = {};
		for (const m of rows) {
			if (inDefaultList(m) && (category === "all" || m.category === category)) c[m.group] = (c[m.group] ?? 0) + 1;
		}
		return c;
	}, [rows, category, showSettled, showFolded]);

	const shown = useMemo(
		() =>
			rows.filter(
				(m) =>
					(category === "all" || m.category === category) &&
					// A search looks across every group; the floors only shape the lists.
					(searching ? matches(q, m.question, m.conditionId, m.eventTitle) : m.group === tab.id && inDefaultList(m)),
			),
		[rows, q, searching, category, tab.id, showSettled, showFolded],
	);

	const body = error && markets.length === 0 ? (
		<SourceError source="Polymarket (gamma-api)" onRetry={() => void mutate()} />
	) : !isLoading && shown.length === 0 ? (
		<Empty>{searching ? "No markets match. Clear the search or category." : "Nothing in this list right now. Try another tab or category."}</Empty>
	) : undefined;

	const scored = searching || tab.id === "ranked";
	const tapeOldest = trades?.length ? trades[trades.length - 1]?.timestamp : null;

	return (
		<View>
			<ViewHeader
				title="unusual markets"
				meta={markets.length ? `${count(shown.length)} ${searching ? "found" : "listed"}` : undefined}
				desc={searching ? "Search covers every list, plus Polymarket's own search for markets not loaded here." : tab.desc}
			>
				<div className="flex basis-full sm:basis-auto">
					<SearchBox value={q} onChange={setQ} placeholder="search markets" />
				</div>
				<Select label="Category" value={category} options={categories} onChange={(v) => setCategory(v)} />
				<Toggle on={showSettled === "1"} onClick={() => setShowSettled(showSettled === "1" ? null : "1")} title="Include markets priced under 1.5¢ or above 98.5¢">
					decided
				</Toggle>
				<Toggle on={showFolded === "1"} onClick={() => setShowFolded(showFolded === "1" ? null : "1")} title="Show every market in an event, not only its highest scorer">
					all in event
				</Toggle>
			</ViewHeader>
			<div role="tablist" aria-label="Market lists" className="no-scrollbar flex shrink-0 gap-px overflow-x-auto">
				{TABS.map((t) => {
					const on = !searching && t.id === tab.id;
					return (
						<button
							key={t.id}
							type="button"
							role="tab"
							aria-selected={on}
							title={t.title}
							onClick={() => setParams({ tab: t.id === "ranked" ? null : t.id, sort: null, q: null })}
							className={`whitespace-nowrap px-2 py-0.5 text-[11px] ${on ? "bg-up font-bold text-black" : "text-dim hover:text-bright"}`}
						>
							{t.label}
							<span className={`num ml-1.5 ${on ? "" : "text-mute"}`}>{markets.length ? count(counts[t.id] ?? 0) : blankText(3)}</span>
						</button>
					);
				})}
			</div>
			<Panel>
				<DataGrid
					label={searching ? "Market search" : `Markets: ${tab.label}`}
					cols={scored ? SCORED_COLS : PLAIN_COLS}
					rows={shown}
					rowKey={(m) => m.conditionId}
					initialSort={scored ? { key: "u", dir: "desc" } : { key: "v", dir: "desc" }}
					csvName={`markets-${tab.id}`}
					narrowRowH={62}
					subline={(m) => `${whySentence(m, m.parts, m.note)}${m.whales > 0 ? ` · ${m.whales} whale` : ""}`}
					onRow={(m) => openMarket(m.conditionId)}
					loading={isLoading && markets.length === 0}
					body={body}
				/>
				<GridNote className="min-h-[73px] sm:min-h-0">
					<Updated at={updatedAt} refreshMs={MARKETS_REFRESH_MS} failing={Boolean(error)} />
					{count(markets.length)} open markets loaded · min {usd(MIN_VOL24)} 24h · source: Polymarket gamma-api
					{tapeOldest ? ` · whale counts: last ${count(trades?.length)} trades of ${usd(WHALE_MIN_USD)}+, back ${ago(tapeOldest)}, so no chip does not mean no whales` : ""}
					{flagged.rows.length ? " · FLAG from PolyGains" : ""}
					{partial ? <span className="text-warn"> · part of the market list failed to load, so scores use fewer peers</span> : null}
				</GridNote>
			</Panel>
		</View>
	);
}
